import { DISPLAY_CONTENT, FREQUENCY_LABELS } from 'modules/globalSms/constants';
import { useQuerySmsPreferencesCountryVersionHistory as useQueryHistory } from 'modules/globalSms/hooks';
import { getFormattedDateTimeText } from 'modules/globalSms/utils';
import { SecondaryButton } from 'ui/button';
import { Avatar } from 'ui/avatar';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { Logo } from 'ui/logo';
import { DATE_FORMAT_FULL_WITH_WEEKDAY_2 } from 'utils/date';
import {
  Line,
  StyledCircle,
  Timeline,
  TimelinePoint,
  VersionContainer,
  VersionContent,
  VersionUpdateUser,
} from '../styles';

const {
  closeButtonLabel,
  contentAlphanumericLabel,
  contentFrequencyLabel,
  contentNumericLabel,
  headerAppend,
  headerCountryFallback,
  iterableLabel,
  versionCreatePrepend,
  versionUpdatePrepend,
} = DISPLAY_CONTENT.historyModal;

type Props = {
  regionCode: string;
  regionName: string;
  isModalOpen: boolean;
  setIsModalOpen: (val: boolean) => void;
};

export const VersionHistoryModal = ({
  regionCode,
  regionName,
  isModalOpen,
  setIsModalOpen,
}: Props) => {
  const { results: countryVersionHistory } = useQueryHistory(regionCode);

  const historyLength = countryVersionHistory?.length || 0;
  const sortedVersionHistory = [...countryVersionHistory]?.sort(
    ({ createdAt: createdAtA }, { createdAt: createdAtB }) => {
      if (createdAtB === undefined) return -1;
      return Number(createdAtB.timestamp) - Number(createdAtA?.timestamp);
    },
  );

  return (
    <Modal
      data-test="version-history-modal"
      id={`version-history-modal-${regionCode.toLowerCase()}`}
      isModalOpen={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      withPortal
    >
      <ModalHeader>{`${regionName || headerCountryFallback} ${headerAppend}`}</ModalHeader>
      <ModalBody>
        <Timeline>
          {sortedVersionHistory.map(
            ({ numericOptOut, alphanumericOptOut, frequency, createdAt, createdBy }, index) => (
              <TimelinePoint
                data-test={`version-history-modal-timeline-point-${index}`}
                key={`version-history-${regionCode.toLowerCase()}-${createdAt?.timestamp}`}
              >
                {historyLength > 1 && <StyledCircle height={8} fill="white" isColorInherited />}
                {index < historyLength - 1 && <Line />}
                <VersionContainer>
                  {createdAt
                    ? getFormattedDateTimeText(createdAt.timestamp, DATE_FORMAT_FULL_WITH_WEEKDAY_2)
                    : iterableLabel}
                  <VersionUpdateUser>
                    {createdBy ? (
                      <Avatar
                        data-test={`version-history-avatar-${index}`}
                        firstName={
                          createdBy.fullName?.split(' ')[0] ||
                          (createdBy.id?.length && createdBy.id.charAt(0)) ||
                          ''
                        }
                        lastName={createdBy.fullName?.split(' ')[1] || ''}
                        imgSrc={createdBy.avatarUrl || ''}
                      />
                    ) : (
                      <Logo data-test="iterable-logo" height={24} />
                    )}
                    {index === historyLength - 1 ? versionCreatePrepend : versionUpdatePrepend}
                    {createdBy?.fullName ?? iterableLabel}
                  </VersionUpdateUser>
                  <VersionContent>
                    {alphanumericOptOut && (
                      <div>
                        {contentAlphanumericLabel}: {alphanumericOptOut}
                      </div>
                    )}
                    <div>
                      {contentNumericLabel}: {numericOptOut}
                    </div>
                    <div>
                      {contentFrequencyLabel}: {FREQUENCY_LABELS[frequency]}
                    </div>
                  </VersionContent>
                </VersionContainer>
              </TimelinePoint>
            ),
          )}
        </Timeline>
      </ModalBody>
      <ModalFooter hasCancel={false}>
        <SecondaryButton onClick={() => setIsModalOpen(false)}>{closeButtonLabel}</SecondaryButton>
      </ModalFooter>
    </Modal>
  );
};
